import { TREATMENT_CATEGORIES_IDS } from '@/constants/category';
import { DoctorMedicineType } from '@/models/common';
import { MedicineType } from '@/types/bff/gql-schema';
import { SearchKoreaAddressV2Vo } from '@/types/hospital';
import { MedicineUnit, Treatment } from '@/types/nonBenefit';
import { formatPriceComma } from '@/utils/format';

// 운영과 데브의 ID가 달라 운영으로 기준 +) 곧 바뀔 예정
export const ISOTINONN_ID = '10';
const CLINDAMYCIN_ID = '16';
export const PINASTERIDES_ID = '73';

export const ACNE_TREATMENT_MEDICINE_UNIT_ID_LIST = [ISOTINONN_ID, CLINDAMYCIN_ID];

export class NonBenefitPriceSummary {
  constructor(
    private _nonBenefitPriceTotalSummary: {
      nationalSummaries: Array<{
        averagePrice: number;
        minPrice: number;
        treatmentMedicineUnitId: string;
        unitDisplayName: string;
        unitTypeDisplayName: string;
      }>;
      regionSummaries: Array<{
        averagePrice: number;
        minPrice: number;
        treatmentMedicineUnitId: string;
        unitDisplayName: string;
        unitTypeDisplayName: string;
      }>;
    }
  ) {}

  get nationalSummaries() {
    return this._nonBenefitPriceTotalSummary.nationalSummaries.slice(0, 3);
  }

  get regionSummaries() {
    return this._nonBenefitPriceTotalSummary.regionSummaries.slice(0, 3);
  }

  getSelectedRegionSummaryByTreatmentMedicineUnitId(treatmentMedicineUnitId: string) {
    return (
      this.regionSummaries.find((summary) => summary.treatmentMedicineUnitId === treatmentMedicineUnitId) ||
      this.regionSummaries[0]
    );
  }

  getSelectedRegionSummaryPriceDisplayGap(treatmentMedicineUnitId: string) {
    const selectedRegionSummary = this.getSelectedRegionSummaryByTreatmentMedicineUnitId(treatmentMedicineUnitId);

    return selectedRegionSummary.averagePrice - selectedRegionSummary.minPrice;
  }

  getComparePriceDisplayGapText(treatmentMedicineUnitId: string) {
    const priceGap = this.getSelectedRegionSummaryPriceDisplayGap(treatmentMedicineUnitId);

    if (priceGap > 0) {
      return `평균가보다 ${formatPriceComma(priceGap)}원 저렴해요`;
    } else if (priceGap < 0) {
      return `평균가보다 ${formatPriceComma(-priceGap)}원 비싸요`;
    } else {
      return '평균가와 동일해요';
    }
  }
}

export class NonBenefitMedicine {
  readonly id: string;
  readonly medicineName: string;
  readonly medicineDescription: string;
  readonly _units: MedicineUnit[];
  readonly treatment: Treatment;

  constructor(medicine: {
    id: string;
    medicineName: string;
    medicineDescription: string;
    units: {
      id: string;
      unit: string;
      unitType: string;
      unitTypeDisplayName: string;
    }[];
    treatment: {
      id: string;
      name: string;
      isBenefit: boolean;
      categoryId: string;
    };
  }) {
    this.id = medicine.id;
    this.medicineName = medicine.medicineName;
    this.medicineDescription = medicine.medicineDescription;
    this._units = medicine.units;
    this.treatment = medicine.treatment;
  }

  get units() {
    return this._units.slice(0, 3).map((unit) => ({
      id: unit.id,
      unit: unit.unit,
      unitType: unit.unitType,
      unitTypeDisplayName: unit.unitTypeDisplayName,
      unitDisplayName: `${unit.unit}${unit.unitTypeDisplayName}`,
    }));
  }

  get medicineDisplayName() {
    return this.medicineName.replace(/\(.*?\)/g, '').trim();
  }

  get medicineGeneralName() {
    return this.medicineName.split('(')[0].trim() ?? '';
  }

  get installQrcodeUrl() {
    switch (this.id) {
      case '1': {
        return 'https://abr.ge/utcfph';
      }
      case '17': {
        return 'https://abr.ge/utcfph';
      }
      case '2': {
        return 'https://abr.ge/oxgiy3';
      }
      case '18': {
        return 'https://abr.ge/oxgiy3';
      }
      case '3': {
        return 'https://abr.ge/uqnpjq';
      }
      case '4': {
        return 'https://abr.ge/wo8txu';
      }
      default: {
        return 'https://abr.ge/x2av4k';
      }
    }
  }

  get deepLinkUrl() {
    switch (this.id) {
      case '1': {
        return 'doctornow://link?to=doctor-list&type=symptom&categoryId=9';
      }
      case '17': {
        return 'doctornow://link?to=doctor-list&type=symptom&categoryId=9';
      }
      case '2': {
        return 'doctornow://link?to=doctor-list&type=symptom&categoryId=12';
      }
      case '18': {
        return 'doctornow://link?to=doctor-list&type=symptom&categoryId=12';
      }
      case '3': {
        return 'doctornow://link?to=doctor-list&type=symptom&categoryId=11';
      }
      case '4': {
        return 'doctornow://link?to=doctor-list&type=symptom&categoryId=11';
      }
      default: {
        return 'doctornow://link?to=home';
      }
    }
  }

  get treatmentProductMedicineName() {
    switch (this.treatment.id) {
      case TREATMENT_CATEGORIES_IDS.Hairloss: {
        return '탈모약';
      }
      case TREATMENT_CATEGORIES_IDS.Acne: {
        return '여드름약';
      }
      case TREATMENT_CATEGORIES_IDS.Diet: {
        return '다이어트주사';
      }
      default: {
        return '';
      }
    }
  }

  get treatmentProductName() {
    switch (this.treatment.id) {
      case TREATMENT_CATEGORIES_IDS.Hairloss: {
        return '탈모';
      }
      case TREATMENT_CATEGORIES_IDS.Acne: {
        return '여드름';
      }
      case TREATMENT_CATEGORIES_IDS.Diet: {
        return '다이어트주사';
      }
      default: {
        return '';
      }
    }
  }

  get treatmentProductShareImageUrl() {
    switch (this.treatment.id) {
      case TREATMENT_CATEGORIES_IDS.Hairloss: {
        return 'https://file.doctornow.co.kr/non-benefit/kakao-share/hair_thumbnail.png';
      }
      case TREATMENT_CATEGORIES_IDS.Acne: {
        return 'https://file.doctornow.co.kr/non-benefit/kakao-share/skin_thumbnail.png';
      }
      case TREATMENT_CATEGORIES_IDS.Diet: {
        return 'https://file.doctornow.co.kr/non-benefit/kakao-share/diet_thumbnail.png';
      }
      default: {
        return 'https://file.doctornow.co.kr/non-benefit/kakao-share/default_thumbnail.png';
      }
    }
  }

  get medicineProductName() {
    switch (this.id) {
      case '1': {
        return '모모페시아, 헤어그로, 모나드, 미노페시아, 마이페시아 등';
      }
      case '17': {
        return '두타윈, 아다모, 듀오다트, 두타텍트, 두테드 등';
      }
      // 기존 탈모약 id 피나:1 두타:17 => 다이어트 약 대면 수령으로 데이터 바뀌면서 피나:1 두타:39 id 변경
      case '39': {
        return '두타윈, 아다모, 듀오다트, 두타텍트, 두테드 등';
      }
      case '2': {
        return '마운자로, 위고비 등';
      }
      case '18': {
        return '마운자로 등';
      }
      case '3': {
        return '트레논, 제로큐탄, 로이탄, 트레인 등 ';
      }
      case '4': {
        return '클린디올겔, 클린디올액 등';
      }
      default: {
        return '';
      }
    }
  }

  get treatmentMedicineSeoTitle() {
    switch (this.treatment.id) {
      case TREATMENT_CATEGORIES_IDS.Hairloss: {
        return '탈모약 가격 성지 약국 찾기 | 피나스테리드(프로페시아·피나온) - 닥터나우';
      }
      case TREATMENT_CATEGORIES_IDS.Acne: {
        return '여드름약 가격 약국 찾기 | 이소트레티노인(이소티논·니메겐 등) - 닥터나우';
      }
      case TREATMENT_CATEGORIES_IDS.Diet: {
        return '위고비·마운자로·오젬픽 등 최저 가격 약국 찾기 - 닥터나우';
      }
      default: {
        return `${this.treatment.name} 가격 성지 약국 찾기 | ${this.medicineProductName ?? '비대면 진료 아플 때'} - 닥터나우`;
      }
    }
  }

  get treatmentMedicineSeoDescription() {
    switch (this.treatment.id) {
      case TREATMENT_CATEGORIES_IDS.Hairloss: {
        return '탈모약(피나스테리드) 가격, 어디가 제일 저렴할까? 닥터나우에서 우리 동네 탈모약 최저가 성지 병원·약국·비대면진료 정보를 비교해보세요.';
      }
      case TREATMENT_CATEGORIES_IDS.Acne: {
        return '여드름약 추천, 처방, 약국 정보. 닥터나우에서 내 근처 여드름약(이소티논, 니메겐 등)의 병원·약국·비대면진료 정보를 비교해보세요.';
      }
      case TREATMENT_CATEGORIES_IDS.Diet: {
        return '위고비, 마운자로, 오젬픽 등 세마글루티드의 최저 가격을 닥터나우 지도에서 비교해 보세요. 가장 싼 약국과 저렴한 병원 처방 정보를 확인할 수 있어요.';
      }
      default: {
        return '가격, 어디가 제일 저렴할까? 닥터나우에서 우리 동네 최저가 성지 병원·약국·비대면진료 정보를 비교해보세요.';
      }
    }
  }

  get treatmentMedicineSeoKeywords() {
    switch (this.treatment.id) {
      case TREATMENT_CATEGORIES_IDS.Hairloss: {
        return '탈모약, 탈모약가격, 탈모성지, 탈모약국, 탈모약최저가, 탈모병원, 탈모비대면, 탈모약비대면처방';
      }
      case TREATMENT_CATEGORIES_IDS.Acne: {
        return '이소티논, 니메겐, 이소트레티노인, 여드름약, 여드름약가격, 여드름약국, 이소티논가격';
      }
      case TREATMENT_CATEGORIES_IDS.Diet: {
        return '위고비, 마운자로, 오젬픽, 위고비처방, 위고비약국찾기, 위고비병원찾기, 위고비가격, 위고비비대면진료, 위고비성지, 다이어트주사';
      }
      default: {
        return `비대면 진료, 원격진료, 온라인 진료, 감기, 탈모, 여드름, 소아과, 인공눈물, ${this.treatment.name}`;
      }
    }
  }
}

export class NonBenefitMedicines {
  constructor(private _medicines: NonBenefitMedicine[]) {}

  get medicines() {
    return this._medicines;
  }

  findMedicineByTreatmentMedicineId(treatmentMedicineId?: string) {
    return this.medicines.find((medicine) => medicine.id === treatmentMedicineId) || this.medicines[0];
  }

  findMedicineByQueryTreatmentMedicineUnitId(treatmentMedicineUnitId?: string) {
    return this.medicines.find((medicine) => medicine.units.some((unit) => unit.id === treatmentMedicineUnitId));
  }

  findMedicineByTreatmentMedicineUnitId(treatmentMedicineUnitId?: string) {
    if (!treatmentMedicineUnitId) {
      return this.medicines[0];
    }

    return (
      this.medicines.find((medicine) => medicine.units.some((unit) => unit.id === treatmentMedicineUnitId)) ||
      this.medicines[0]
    );
  }

  findMedicineUnitByTreatmentMedicineUnitId(treatmentMedicineUnitId?: string) {
    const selectedMedicine = this.findMedicineByTreatmentMedicineUnitId(treatmentMedicineUnitId);

    if (!treatmentMedicineUnitId) {
      return selectedMedicine.units[0];
    }
    return selectedMedicine.units.find((unit) => unit.id === treatmentMedicineUnitId) || selectedMedicine.units[0];
  }
}

export class Region {
  private seoulAddress = {
    centroidLatitude: 37.55191888765841,
    centroidLongitude: 126.99182510819786,
    displayName: '서울',
    regionCode: '11',
    regionName: '서울특별시',
  };

  private sejongRegionCode = '36';

  constructor(private regionCode?: string) {}

  excludeSejongSiDoRegionCode() {
    return this.isSejongRegion ? '' : this.regionCodes.siDoCode;
  }
  excludeSejongSiGunGuRegionCode() {
    return this.isSejongRegion ? null : this.regionCodes.siGunGuCode;
  }
  get isSejongRegion() {
    return this.regionCode?.slice(0, 2) === this.sejongRegionCode;
  }

  private get regionCodeLength() {
    return this.regionCode?.length ?? 0;
  }
  get regionCodes() {
    if (!this.regionCode) {
      return {
        siDoCode: this.seoulAddress.regionCode,
        siGunGuCode: '',
        dongCode: '',
      };
    }

    /* 지역코드가 시도2자리, 시군구 5자리, 동읍리 8자리로 이루어져있음 */
    switch (this.regionCodeLength) {
      case 2:
        return {
          siDoCode: this.regionCode,
          siGunGuCode: '',
          dongCode: '',
        };
      case 5:
        return {
          siDoCode: this.regionCode.slice(0, 2),
          siGunGuCode: this.isSejongRegion ? '' : this.regionCode,
          dongCode: '',
        };
      case 8:
        return {
          siDoCode: this.regionCode.slice(0, 2),
          siGunGuCode: this.isSejongRegion ? '' : this.regionCode.slice(0, 5),
          dongCode: this.regionCode,
        };
      /* 예외 상황일 때 초기화 */
      default:
        return {
          siDoCode: this.seoulAddress.regionCode,
          siGunGuCode: '',
          dongCode: '',
        };
    }
  }
}
export class KoreaAddress {
  private doNames: Record<string, string> = {
    경기도: '경기',
    강원도: '강원',
    충청북도: '충북',
    충청남도: '충남',
    경상북도: '경북',
    경상남도: '경남',
    전라북도: '전북',
    전라남도: '전남',
  };
  constructor(
    private _siDoList: SearchKoreaAddressV2Vo[],
    private _siGunGuList: SearchKoreaAddressV2Vo[],
    private _dongList: SearchKoreaAddressV2Vo[]
  ) {}

  get siDoList() {
    return this._siDoList;
  }
  get siGunGuList() {
    return this._siGunGuList;
  }
  get dongList() {
    return this._dongList;
  }

  getSelectedRegionByDepth(queryRegion: Region) {
    const { siDoCode, siGunGuCode, dongCode } = queryRegion.regionCodes;
    return {
      siDo: this.siDoList.find((sido) => sido.regionCode === siDoCode) || null,
      siGunGu: this.siGunGuList.find((siGunGu) => siGunGu.regionCode === siGunGuCode) || null,
      dong: this.dongList.find((dong) => dong.regionCode === dongCode) || null,
    };
  }

  getAddressTitle(queryRegion: Region) {
    const { siDo, siGunGu, dong } = this.getSelectedRegionByDepth(queryRegion);

    if (!dong && !siGunGu) {
      if (!siDo?.regionName) {
        return '';
      } else {
        return this.doNames[siDo.regionName] ?? siDo.regionName.slice(0, 2);
      }
    }

    if (!dong) {
      return siGunGu?.regionName ?? '';
    }

    return dong?.regionName ?? '';
  }

  getFullAddressTitle(queryRegion: Region) {
    const { siDo, siGunGu, dong } = this.getSelectedRegionByDepth(queryRegion);

    if (!dong && !siGunGu) {
      return siDo?.regionName ?? '';
    }

    if (!dong) {
      return `${siDo?.regionName} ${siGunGu?.regionName}`;
    }

    return `${siDo?.regionName} ${siGunGu?.regionName} ${dong?.regionName}`;
  }
}

export class NonBenefitNonF2fDoctor {
  constructor(
    private _doctor: {
      doctorId: string;
      name: string;
      hospitalName: string;
      hospitalAddress: {
        jibunAddress: string | null;
        roadAddress: string;
        regionByDepth: {
          siDoName: string;
          siGunGuName: string;
          legalEupMyeonDongName: string;
        };
      };
      publicId: string;
      nonBenefitTreatments: {
        nonBenefitTreatmentUnitInfos: {
          originalPrice: number;
          unitDisplayName: string;
          treatmentMedicineUnitId: string;
        }[];
      }[];
      medicineType: MedicineType;
      profileImageUrl: string;
      readyForVideoCall: boolean;
      departments: Array<{ name: string; isProfessional: boolean }>;
    }
  ) {}

  private get doctorMedicineType() {
    switch (this._doctor.medicineType) {
      case MedicineType.Dentist:
        return DoctorMedicineType.Dentist;
      case MedicineType.Doctor:
        return DoctorMedicineType.Doctor;
      case MedicineType.OrientalDoctor:
        return DoctorMedicineType.OrientalDoctor;
      default:
        return DoctorMedicineType.Unknown;
    }
  }

  private get isOrientalDoctor() {
    return this.doctorMedicineType === DoctorMedicineType.OrientalDoctor;
  }

  get doctorId() {
    return this._doctor.doctorId;
  }

  get publicId() {
    return this._doctor.publicId;
  }

  get doctorDisplayName() {
    return `${this.name} ${this.isOrientalDoctor ? '한의사' : '의사'}`;
  }
  private get name() {
    return this._doctor.name;
  }
  get hospitalName() {
    return this._doctor.hospitalName;
  }
  get hospitalAddress() {
    if (!this._doctor.hospitalAddress.regionByDepth.siGunGuName) {
      return `${this._doctor.hospitalAddress.regionByDepth.siDoName} ${this._doctor.hospitalAddress.regionByDepth.legalEupMyeonDongName}`;
    }
    return `${this._doctor.hospitalAddress.regionByDepth.siDoName} ${this._doctor.hospitalAddress.regionByDepth.siGunGuName} ${this._doctor.hospitalAddress.regionByDepth.legalEupMyeonDongName}`;
  }

  get profileImageUrl() {
    return this._doctor.profileImageUrl;
  }
  get readyForVideoCall() {
    return this._doctor.readyForVideoCall;
  }
  get professionalDepartments() {
    return this._doctor.departments.filter((department) => department.isProfessional);
  }

  getNonBenefitTreatmentPriceByTreatmentMedicineUnitId(treatmentMedicineUnitId: string) {
    const foundTreatment = this._doctor.nonBenefitTreatments.find((treatment) => {
      return treatment.nonBenefitTreatmentUnitInfos.some(
        (unitInfo) => unitInfo.treatmentMedicineUnitId === treatmentMedicineUnitId
      );
    });

    return (
      foundTreatment?.nonBenefitTreatmentUnitInfos.filter(
        (unitInfo) => unitInfo.treatmentMedicineUnitId === treatmentMedicineUnitId
      )[0].originalPrice ?? 0
    );
  }

  getNonBenefitTreatmentPriceTextByTreatmentMedicineUnitId(treatmentMedicineUnitId: string) {
    const foundTreatmentPrice = this.getNonBenefitTreatmentPriceByTreatmentMedicineUnitId(treatmentMedicineUnitId);

    if (!foundTreatmentPrice) {
      return '확인중';
    }

    return `${formatPriceComma(foundTreatmentPrice)}원`;
  }

  isCheapestTreatmentPrice(treatmentMedicineUnitId: string, regionCheapestPrice: number) {
    const foundTreatmentPrice = this.getNonBenefitTreatmentPriceByTreatmentMedicineUnitId(treatmentMedicineUnitId);
    return foundTreatmentPrice === regionCheapestPrice;
  }
}
